/**
 * 데이터 훅.
 *
 * 상태관리 라이브러리를 넣지 않았다. 콘솔의 데이터 흐름은 "목록을 불러오고, 바꾸고,
 * 다시 불러온다"가 전부다. 그 정도에 라이브러리를 얹으면 배우고 유지할 것만 늘어난다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiCallError } from './api/client.js';
import { notify } from './components/Toast.js';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** 다시 불러온다. 변경 후 목록을 갱신할 때 쓴다. */
  reload: () => void;
}

/**
 * 비동기 로드.
 *
 * 언마운트 후 setState 를 막는다 — 콘솔은 화면 전환이 잦고, 그때마다 경고가 뜨면
 * 진짜 문제를 가린다.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: readonly unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    loader()
      .then((result) => {
        if (cancelled || !alive.current) return;
        setData(result);
      })
      .catch((cause: unknown) => {
        if (cancelled || !alive.current) return;
        setError(describeError(cause));
      })
      .finally(() => {
        if (cancelled || !alive.current) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, loading, error, reload };
}

/**
 * 쓰기 동작.
 *
 * 성공·실패 메시지를 한 곳에서 만들어 화면마다 다른 문구가 나오지 않게 한다.
 * 사용자는 같은 실패를 화면마다 다른 말로 듣고 싶어 하지 않는다.
 */
export function useAction(options: ErrorOptions = {}): {
  /**
   * `success` 를 주면 끝났을 때 화면 아래에 한 줄 알린다(`notify`). 결과에 따라 말이 다르면 함수로,
   * 알릴 것이 없으면 null 을 돌려준다.
   */
  run: <T>(
    action: () => Promise<T>,
    onSuccess?: (result: T) => void,
    success?: string | ((result: T) => string | null),
  ) => Promise<void>;
  pending: boolean;
  error: string | null;
  clearError: () => void;
} {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async <T,>(
    action: () => Promise<T>,
    onSuccess?: (result: T) => void,
    success?: string | ((result: T) => string | null),
  ) => {
    setPending(true);
    setError(null);
    try {
      const result = await action();
      onSuccess?.(result);
      const message = typeof success === 'function' ? success(result) : success;
      if (message) notify(message);
    } catch (cause) {
      setError(describeError(cause, options));
    } finally {
      setPending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.rawAuthMessage]);

  return { run, pending, error, clearError: () => setError(null) };
}

/**
 * 오류를 사람이 읽고 **행동할 수 있는** 문장으로 바꾼다.
 *
 * 서버는 참조 무결성 위반에 어디에서 쓰이는지를 details 로 준다. 그걸 그대로
 * 버리면 사용자는 "삭제할 수 없습니다"만 보고 무엇을 해야 할지 모른다.
 */
export interface ErrorOptions {
  /**
   * 401 을 "인증 만료"로 바꾸지 않고 서버 메시지를 그대로 쓴다.
   *
   * 로그인 폼에서 필요하다. 거기서 401 은 "비밀번호가 틀렸다"는 뜻인데,
   * "새로고침하세요"라고 말하면 사용자를 엉뚱한 데로 보낸다.
   */
  rawAuthMessage?: boolean;
}

export function describeError(cause: unknown, options: ErrorOptions = {}): string {
  if (cause instanceof ApiCallError) {
    const details = cause.details as
      | {
          playlists?: Array<{ name: string }>;
          displays?: number;
          display_count?: number;
          missing?: string[];
          /** 줄마다 읽은 결과 — 환경 설정의 허용 주소처럼 여러 줄을 한 번에 거절할 때. */
          problems?: string[];
        }
      | undefined;

    if (details?.problems?.length) return `${cause.message}: ${details.problems.join(' · ')}`;
    // 쓰는 곳 목록이 온 오류는 문장에 이미 이름이 들어 있다(`services/references.ts`) — 개수를 덧붙이지 않는다.
    if (Array.isArray((cause.details as { references?: unknown } | undefined)?.references)) return cause.message;

    if (details?.playlists?.length) {
      const names = details.playlists.map((p) => p.name).join(', ');
      return `${cause.message} (사용 중: ${names})`;
    }
    // 화면 수를 주는 키가 두 가지다 — 자산·플레이리스트는 displays, 스케줄은 display_count.
    const displayCount = details?.displays ?? details?.display_count;
    if (typeof displayCount === 'number' && displayCount > 0) {
      return `${cause.message} (화면 ${displayCount}대가 사용 중)`;
    }
    if (cause.status === 401 && !options.rawAuthMessage) {
      return '로그인이 끊겼습니다. 로그인 화면으로 돌아갑니다.';
    }
    if (cause.status === 403) return `권한이 없습니다: ${cause.message}`;
    if (cause.status === 0) return '서버에 연결할 수 없습니다.';
    return cause.message;
  }
  return cause instanceof Error ? cause.message : '알 수 없는 오류';
}

/**
 * 주기적으로 다시 불러온다. 함대 상태처럼 계속 변하는 값에 쓴다.
 *
 * `enabled` 로 끌 수 있다 — 지금이 아닌 시각을 보고 있는 화면처럼, 다시 불러와도
 * 같은 답이 오는 자리에서 서버를 계속 두드릴 이유가 없다.
 */
/**
 * 주기적으로 다시 불러온다.
 *
 * **탭이 뒤로 물러나면 멈춘다.** 모니터링은 아침에 열어 두고 하루 종일 켜 놓는
 * 화면이고, 그 시간의 대부분은 다른 탭을 보는 중이다. 멈추지 않으면 아무도 보지
 * 않는 동안 서버가 화면 수만큼 편성을 계속 해석한다.
 *
 * 실제 송출 그림이 붙은 뒤로는 이 절약이 서버에서 끝나지 않는다 — 조회가 곧
 * "보고 있다"는 신호라, 멈추면 현장의 화면들도 촬영을 멈춘다.
 *
 * 돌아오면 즉시 한 번 부른다. 주기를 기다리게 하면 탭을 전환한 사람이 가장 먼저
 * 보는 것이 낡은 화면이 된다.
 */
export function usePolling(reload: () => void, intervalMs: number, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const start = (): void => {
      if (timer !== null) return;
      timer = setInterval(reload, intervalMs);
    };
    const stop = (): void => {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    };

    const onVisibility = (): void => {
      if (document.hidden) {
        stop();
        return;
      }
      reload();
      start();
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [reload, intervalMs, enabled]);
}

/**
 * 입력이 멎을 때까지 값을 붙든다.
 *
 * 검색창은 글자마다 서버를 때리면 안 된다 — 목록 API 는 한 번에 수천 건을 훑는다.
 */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return settled;
}

/**
 * 미디어 쿼리가 지금 맞는가 — 창 크기가 바뀌면 따라 바뀐다.
 *
 * CSS 만으로 갈라지지 않는 것에 쓴다. 사이드바의 "접힘" 은 넓은 화면에서 아이콘만 남기는
 * 것인데, 좁은 화면의 서랍이 같은 값을 따르면 서랍을 열어도 글자 없는 아이콘만 보인다.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => mediaMatches(query));

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const onChange = (): void => setMatches(list.matches);
    onChange();
    // 사파리 13 까지는 MediaQueryList 에 addEventListener 가 없다.
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    }
    list.addListener(onChange);
    return () => list.removeListener(onChange);
  }, [query]);

  return matches;
}

function mediaMatches(query: string): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  } catch {
    return false;
  }
}
